// src/components/modals/BaristaOrdersModal.jsx
// Modal to display barista orders in simple format

import { useState, useEffect, useCallback, useRef } from 'react'
import { X, Truck, MapPin, Utensils, Package, Clock, ChefHat, RefreshCw, Search } from 'lucide-react'
import BaristaService from '../../services/baristaService'
import toast from 'react-hot-toast'
import { cn } from '../../utils/cn'

const BaristaOrdersModal = ({ isOpen, onClose, user }) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useRef(null)

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true)
      
      const params = {
        sortBy: 'createdAt',
        limit: 20
        // Fetch all orders for debugging - we can filter in UI if needed
      }
      
      // Use user's branch if available
      if (user?.branchId) {
        params.branchId = user.branchId
      }
      
      const response = await BaristaService.getBaristaOrders(params)
      
      let fetchedOrders = []
      if (response?.success && response?.data && response?.data.baristaOrders) {
        fetchedOrders = response.data.baristaOrders
      } else if (response?.baristaOrders) {
        // Fallback in case API client already extracted data
        fetchedOrders = response.baristaOrders
      } else {
        fetchedOrders = []
      }
      
      setOrders(fetchedOrders)
    } catch (error) {
      console.error('Error fetching orders:', error)
      toast.error('Failed to load orders')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [user?.branchId])

  // Load orders when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('') // Clear search when modal opens
      fetchOrders()
      // Focus on search input after modal opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, fetchOrders])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return
      
      // Focus search on Ctrl/Cmd + F
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
      
      // Clear search on Escape if search is focused
      if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        setSearchTerm('')
        searchInputRef.current?.blur()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  // Get order type icon
  const getOrderTypeIcon = (orderType) => {
    switch (orderType) {
      case 'delivery':
        return <Truck className="h-4 w-4 text-blue-600" />
      case 'dine_in':
        return <Utensils className="h-4 w-4 text-green-600" />
      case 'takeaway':
      default:
        return <Package className="h-4 w-4 text-orange-600" />
    }
  }

  // Get order type label
  const getOrderTypeLabel = (orderType) => {
    switch (orderType) {
      case 'delivery':
        return 'Delivery'
      case 'dine_in':
        return 'Dine In'
      case 'takeaway':
      default:
        return 'Take Away'
    }
  }

  // Get status display
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'preparing':
        return { 
          label: 'Preparing', 
          color: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
          badgeColor: 'bg-yellow-500'
        }
      case 'ready':
        return { 
          label: 'Ready', 
          color: 'bg-green-100 text-green-800 border border-green-200',
          badgeColor: 'bg-green-500'
        }
      case 'pending':
        return { 
          label: 'Pending', 
          color: 'bg-orange-100 text-orange-800 border border-orange-200',
          badgeColor: 'bg-orange-500'
        }
      case 'completed':
        return { 
          label: 'Completed', 
          color: 'bg-blue-100 text-blue-800 border border-blue-200',
          badgeColor: 'bg-blue-500'
        }
      default:
        return { 
          label: status, 
          color: 'bg-gray-100 text-gray-800 border border-gray-200',
          badgeColor: 'bg-gray-500'
        }
    }
  }

  // Filter orders based on search term
  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true
    
    const orderInfo = order.order
    const orderNumber = orderInfo?.orderNumber?.replace('ORD', '') || order._id.slice(-6)
    const customerName = orderInfo?.customer?.name || ''
    
    return (
      orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order._id.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  // Group orders by date
  const groupOrdersByDate = (orders) => {
    const groups = {}
    
    orders.forEach(order => {
      const orderInfo = order.order
      const orderDate = orderInfo?.orderDate ? new Date(orderInfo.orderDate) : new Date()
      const dateKey = orderDate.toDateString() // Format: "Mon Sep 11 2023"
      
      if (!groups[dateKey]) {
        groups[dateKey] = []
      }
      groups[dateKey].push(order)
    })
    
    // Sort groups by date (most recent first)
    const sortedGroups = Object.keys(groups)
      .sort((a, b) => new Date(b) - new Date(a))
      .map(dateKey => ({
        date: dateKey,
        orders: groups[dateKey].sort((a, b) => {
          // Sort orders within each day by time (most recent first)
          const timeA = new Date(a.order?.orderDate || a.createdAt)
          const timeB = new Date(b.order?.orderDate || b.createdAt)
          return timeB - timeA
        })
      }))
    
    return sortedGroups
  }

  const groupedOrders = groupOrdersByDate(filteredOrders)

  // Get formatted date display
  const getDateDisplay = (dateString) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    // Check if it's today
    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    }
    
    // Check if it's yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    }
    
    // For other dates, show day and date
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] m-4 relative z-[99999]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <ChefHat className="h-6 w-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">Barista Orders Overview</h2>
            {orders.length > 0 && (
              <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
                {orders.length}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchOrders}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center space-x-1"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              <span>Refresh</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by order number, customer name, or order ID... (Ctrl+F to focus)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-xs text-gray-500 mt-1">
              Showing {filteredOrders.length} of {orders.length} orders
            </p>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 text-orange-600 animate-spin mx-auto mb-2" />
                <p className="text-gray-500">Loading orders...</p>
              </div>
            </div>
          ) : !Array.isArray(orders) || orders.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <ChefHat className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No active orders found</p>
              </div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Search className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No orders match your search</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-orange-600 hover:text-orange-700 text-sm mt-2 underline"
                >
                  Clear search
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {groupedOrders.map((dateGroup) => (
                <div key={dateGroup.date} className="space-y-4">
                  {/* Date Header */}
                  <div className="flex items-center justify-center">
                    <div className="bg-gray-100 px-6 py-2 rounded-full border">
                      <h3 className="text-lg font-semibold text-gray-700 text-center">
                        {getDateDisplay(dateGroup.date)}
                      </h3>
                    </div>
                  </div>

                  {/* Orders Grid for this date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {dateGroup.orders.map((order) => {
                      const orderInfo = order.order
                      const orderNumber = orderInfo?.orderNumber || order._id.slice(-8).toUpperCase()
                      const statusDisplay = getStatusDisplay(order.status)
                      
                      return (
                        <div
                          key={order._id}
                          className={cn(
                            "bg-white border-2 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200",
                            order.status === 'ready' && "border-green-200 bg-green-50",
                            order.status === 'preparing' && "border-yellow-200 bg-yellow-50",
                            order.status === 'pending' && "border-orange-200 bg-orange-50",
                            order.status === 'completed' && "border-blue-200 bg-blue-50",
                            !['ready', 'preparing', 'pending', 'completed'].includes(order.status) && "border-gray-200"
                          )}
                        >
                          {/* Line 1: Order ID */}
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-lg font-bold text-gray-900">{orderNumber}</span>
                            <div className={cn("w-3 h-3 rounded-full", statusDisplay.badgeColor)}></div>
                          </div>

                          {/* Line 2: Status, Type, and Time */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className={cn("px-2 py-1 rounded-full text-xs font-medium", statusDisplay.color)}>
                                {statusDisplay.label}
                              </span>
                              {getOrderTypeIcon(orderInfo?.orderType)}
                              <span className="text-sm text-gray-600">
                                {getOrderTypeLabel(orderInfo?.orderType)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {orderInfo?.orderDate ? new Date(orderInfo.orderDate).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                }) : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BaristaOrdersModal
